/**
 * Open-source map fallback (Leaflet + CartoDB/OSM tiles — no API key).
 *
 * Rendered whenever Google Maps is unavailable: no key configured, the script
 * fails to load, or Google rejects the key at render time (RefererNotAllowed /
 * InvalidKey / billing) via gm_authFailure. Tiles come from CartoDB's free basemap
 * (positron in light mode, dark_all in dark) so the map always works, offline of
 * Google. Colored circle markers mirror the Google map's price coloring.
 */
import React, { useEffect, useMemo, useRef } from "react";
import { useState } from "react";
import { MapContainer, TileLayer, CircleMarker, Circle, Marker, Popup, Tooltip, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { stateCoordinates, heightToZoom } from "@/lib/stateCoordinates";
import { gradeColor } from "@/lib/grades";
import { ownershipBucket } from "@/lib/mapFilters";

const OWNERSHIP_LABEL: Record<string, string> = {
  nonprofit: "Nonprofit",
  forprofit: "For-profit",
  government: "Government",
};

export interface FallbackHospital {
  hospital_id: string;
  hospital_name: string;
  latitude: number;
  longitude: number;
  city?: string;
  state?: string;
  gross_charge?: number;
  color?: string; // pre-computed marker color; else derived from price vs avg
  /** Letter grade (A–F) — when present the marker renders as a grade badge. */
  grade?: string | null;
  /** National ranking (1 = cheapest) shown as "#rank of totalRanked". */
  rank?: number | null;
  /** Blended scenario price for this hospital (scenario mode pill/popup). */
  scenarioPrice?: number | null;
  /** Filtered out by the consumer filters — render at 25% opacity. */
  dimmed?: boolean;
  // Summary-card fields — each row renders only when the value exists.
  cmsStars?: number | null;
  /** CMS comparative quality: "Above/Same as/Below the national average". */
  cmsSafety?: string | null;
  cmsMortality?: string | null;
  cmsReadmission?: string | null;
  cmsExperience?: string | null;
  googleRating?: number | null;
  googleReviews?: number | null;
  ownership?: string | null;
  hospitalType?: string | null;
  bedCount?: number | null;
  emergencyServices?: boolean | null;
  hospitalSystem?: string | null;
}

interface Props {
  hospitals: FallbackHospital[];
  avgPrice?: number;
  dark?: boolean;
  heightClass?: string;      // tailwind height, default h-full
  legendSide?: "left" | "right"; // legend corner (default right); left when a panel floats over the right edge
  priceLabel?: string;       // tag after a scenarioPrice in the popup (default "this scenario"; the City view passes the code)
  onSelect?: (hospitalId: string) => void;
  /** Optional: derive color from price vs national avg (matches Google map). */
  colorFor?: (grossCharge: number, avgPrice: number) => string;
  /** Show the price as a permanent label on each dot (default true). */
  showPriceLabels?: boolean;
  /** Legend swatches; defaults to the price-vs-avg scale. Pass [] to hide. */
  legend?: Array<{ color: string; label: string }>;
  /** Legend heading (default "Price vs national avg"). */
  legendTitle?: string;
  /** Leaflet wheel zoom; pass false on long pages where the map hijacks scroll. */
  scrollWheelZoom?: boolean;
  /**
   * Deep-link framing (mirrors the Google map's flyTo behavior). When a specific
   * hospital is selected, the map flies to it, zoomed in. Otherwise, if a state
   * is given, the map frames that state. With neither, it fits all the points.
   */
  focusHospital?: { latitude: number; longitude: number; zoom?: number } | null;
  /** Two-letter state code to frame when no hospital is focused. */
  focusState?: string | null;
  /** Universe size for the rank pill ("#123 of 5,400"). */
  totalRanked?: number | null;
  /** Currently selected hospital id — its badge gets the highlight ring. */
  selectedId?: string | null;
  /** Suppress Leaflet popups (mobile uses a bottom sheet instead). */
  disablePopups?: boolean;
  /** Fired when the map background (not a marker) is clicked/tapped. */
  onBackgroundClick?: () => void;
  /** Dashed ring of this many straight-line miles around focusHospital — the
   *  hospital City view's "within N miles" band. */
  focusRadiusMiles?: number | null;
  focusRadiusCenter?: { latitude: number; longitude: number } | null; // ring centre when it differs from focusHospital (City view: ring stays on "here" while a row is centred)
  openPopupId?: string | null;   // open this hospital's popup (City view row click)
}

/** Bridges Leaflet's map-level click to the parent (background taps only —
 *  divIcon Markers don't bubble, and CircleMarkers are set not to). */
function MapClickBridge({ onClick }: { onClick: () => void }) {
  const map = useMap();
  useEffect(() => {
    const h = () => onClick();
    map.on("click", h);
    return () => { map.off("click", h); };
  }, [map, onClick]);
  return null;
}

/**
 * Imperatively drives the map view — react-leaflet's <MapContainer center/zoom>
 * are initial-mount only, so the map "stays still" when the route changes. This
 * child lives inside the container, grabs the map via useMap(), and moves it
 * whenever the framing target changes:
 *   1. a focused hospital  → fly in close (like Google's panToHospital)
 *   2. a state code        → frame the state (like Google's flyToState)
 *   3. otherwise           → fit all valid points (national / heatmap view)
 * First move on mount is instant (setView/fitBounds); later moves animate.
 */
function MapViewController({
  valid, focusHospital, focusState,
}: {
  valid: FallbackHospital[];
  focusHospital?: { latitude: number; longitude: number; zoom?: number } | null;
  focusState?: string | null;
}) {
  const map = useMap();
  const firstMoveRef = useRef(true);

  // Stable id-signature of the fittable points so the fit branch re-runs only
  // when the actual set of hospitals changes — not on every parent re-render
  // (the parent rebuilds the hospitals array inline each render).
  const boundsKey = useMemo(
    () => valid.map(h => h.hospital_id).sort().join(","),
    [valid]
  );

  const focusLat = focusHospital?.latitude ?? null;
  const focusLng = focusHospital?.longitude ?? null;
  const focusZoom = focusHospital?.zoom ?? null;

  // A single signature describing the active target. Keying the effect on this
  // means we move once per real target change: framing a state and then loading
  // its hospitals won't re-trigger a redundant fly.
  const targetSig = useMemo(() => {
    if (focusLat != null && focusLng != null && !isNaN(focusLat) && !isNaN(focusLng)) {
      return `point:${focusLat},${focusLng},${focusZoom ?? 13}`;
    }
    const sc = focusState ? stateCoordinates[focusState.toUpperCase()] : undefined;
    if (sc) return `state:${focusState!.toUpperCase()}`;
    return `bounds:${boundsKey}`;
  }, [focusLat, focusLng, focusZoom, focusState, boundsKey]);

  useEffect(() => {
    const instant = firstMoveRef.current;
    const goTo = (lat: number, lng: number, zoom: number) => {
      if (instant) map.setView([lat, lng], zoom, { animate: false });
      else map.flyTo([lat, lng], zoom, { duration: 1.1 });
      firstMoveRef.current = false;
    };

    // 1. Specific hospital deep-link → zoom right in.
    if (focusLat != null && focusLng != null && !isNaN(focusLat) && !isNaN(focusLng)) {
      goTo(focusLat, focusLng, focusZoom ?? 13);
      return;
    }
    // 2. State view → frame the state.
    if (focusState) {
      const sc = stateCoordinates[focusState.toUpperCase()];
      if (sc) { goTo(sc.latitude, sc.longitude, heightToZoom(sc.height)); return; }
    }
    // 3. Otherwise fit all valid points. National view: fit CONUS when it holds
    //    most points — otherwise Alaska/Hawaii stretch the bounds and the lower
    //    48 collapse into a corner of the viewport.
    if (valid.length === 1) {
      goTo(valid[0].latitude, valid[0].longitude, 11);
    } else if (valid.length > 1) {
      const conus = valid.filter(h => h.latitude >= 24 && h.latitude <= 49.5 && h.longitude >= -125 && h.longitude <= -66);
      const fitPts = conus.length >= valid.length * 0.6 ? conus : valid;
      const bounds = L.latLngBounds(fitPts.map(h => [h.latitude, h.longitude] as [number, number]));
      if (bounds.isValid()) {
        if (instant) map.fitBounds(bounds, { padding: [40, 40], maxZoom: 12 });
        else map.flyToBounds(bounds, { padding: [40, 40], maxZoom: 12, duration: 1.1 });
        firstMoveRef.current = false;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetSig]);

  return null;
}

/**
 * Buckets the current zoom into a class on the Leaflet container so the grade
 * badges scale and the rank pills reveal purely via CSS — no marker re-render
 * on zoom (matters with ~1000 divIcon markers).
 *   osm-zoom-sm  (< 5.5)  small letter badge only
 *   osm-zoom-md  (5.5–6.5) full-size letter badge
 *   osm-zoom-lg  (≥ 6.5)  badge + "#rank of total" pill
 */
function ZoomClassController() {
  const map = useMap();
  useEffect(() => {
    const apply = () => {
      const z = map.getZoom();
      const el = map.getContainer();
      el.classList.remove("osm-zoom-sm", "osm-zoom-md", "osm-zoom-lg");
      el.classList.add(z >= 6.5 ? "osm-zoom-lg" : z >= 5.5 ? "osm-zoom-md" : "osm-zoom-sm");
    };
    apply();
    map.on("zoomend", apply);
    return () => { map.off("zoomend", apply); };
  }, [map]);
  return null;
}

// Compact money: $22k, $1.2k, $850.
function abbrevMoney(n: number): string {
  if (n >= 100000) return `$${Math.round(n / 1000)}k`;
  if (n >= 10000) return `$${(n / 1000).toFixed(0)}k`;
  if (n >= 1000) return `$${(n / 1000).toFixed(1)}k`;
  return `$${Math.round(n)}`;
}

const DEFAULT_LEGEND = [
  { color: "#22c55e", label: "Well below avg" },
  { color: "#84cc16", label: "Below avg" },
  { color: "#eab308", label: "Near avg" },
  { color: "#f97316", label: "Above avg" },
  { color: "#dc2626", label: "Far above avg" },
];

const DEFAULT_COLOR_FOR = (gross: number, avg: number): string => {
  const d = avg > 0 ? ((gross - avg) / avg) * 100 : 0;
  if (d <= -25) return "#22c55e";
  if (d <= 0) return "#84cc16";
  if (d <= 25) return "#eab308";
  if (d <= 50) return "#f59e0b";
  if (d <= 100) return "#f97316";
  return "#dc2626";
};

// Compact count: 5400 → "5.4k" keeps the rank pill short at any universe size.
function abbrevCount(n: number): string {
  return n >= 1000 ? `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k` : String(n);
}

export default function OpenStreetMapFallback({
  hospitals, avgPrice = 0, dark, heightClass = "h-full", onSelect, colorFor = DEFAULT_COLOR_FOR, legendSide = "right", priceLabel = "this scenario",
  showPriceLabels = true, legend = DEFAULT_LEGEND, legendTitle = "Price vs national avg", scrollWheelZoom = true,
  focusHospital = null, focusState = null, totalRanked = null, selectedId = null,
  disablePopups = false, onBackgroundClick, focusRadiusMiles = null, focusRadiusCenter = null, openPopupId = null,
}: Props) {
  const [placing, setPlacing] = useState(true);
  const valid = useMemo(
    () => hospitals.filter(h => h.latitude && h.longitude && !isNaN(h.latitude) && !isNaN(h.longitude) && (h.latitude !== 0 || h.longitude !== 0)),
    [hospitals]
  );
  // Permanent price labels only when the map isn't too dense (else they overlap
  // into an unreadable wall). Above the cap, prices still show on click/hover.
  const priceLabelsOn = showPriceLabels && valid.filter(h => (h.gross_charge ?? 0) > 0).length <= 120;

  // Center on the mean of valid CONUS points, else geographic center of the US.
  const center = useMemo<[number, number]>(() => {
    const conus = valid.filter(h => h.latitude >= 24 && h.latitude <= 49 && h.longitude >= -125 && h.longitude <= -66);
    const pts = conus.length ? conus : valid;
    if (!pts.length) return [39.5, -98.35];
    const la = pts.reduce((s, h) => s + h.latitude, 0) / pts.length;
    const lo = pts.reduce((s, h) => s + h.longitude, 0) / pts.length;
    return [la, lo];
  }, [valid]);

  // OpenStreetMap's own tile server — keyless. (CARTO's basemap CDN started
  // requiring an API key 2026-08 and serves "API KEY REQUIRED" watermark tiles
  // without one.) OSM has no dark variant; dark mode inverts the tile pane
  // via the CSS filter below.
  const tileUrl = "https://tile.openstreetmap.org/{z}/{x}/{y}.png";
  const attribution = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

  const panelBg = dark ? "rgba(15,29,47,0.92)" : "rgba(255,255,255,0.94)";
  const panelBorder = dark ? "rgba(197,165,90,0.3)" : "rgba(0,40,104,0.15)";
  const panelInk = dark ? "#f0ebe3" : "#1B1B1B";
  const panelMuted = dark ? "rgba(240,235,227,0.6)" : "rgba(27,27,27,0.62)";

  // Row-click from a side panel: open that hospital's popup after the fly-to
  // settles. Both marker kinds bind their <Popup> child, so openPopup works.
  const markerRefs = useRef(new Map<string, { openPopup: () => void }>());
  useEffect(() => {
    if (!openPopupId) return;
    const t = setTimeout(() => { try { markerRefs.current.get(openPopupId)?.openPopup(); } catch { /* marker gone */ } }, 350);
    return () => clearTimeout(t);
  }, [openPopupId]);

  return (
    <div className={`relative w-full ${heightClass}`} style={{ minHeight: 300 }}>
      {/* Style the permanent price labels: small pill, no arrow, theme-aware. */}
      <style>{`
        .osm-price-label.leaflet-tooltip {
          background: ${dark ? "rgba(15,29,47,0.9)" : "rgba(255,255,255,0.95)"};
          color: ${panelInk};
          border: 1px solid ${panelBorder};
          border-radius: 6px; padding: 1px 5px; font-size: 10px; font-weight: 700;
          box-shadow: 0 1px 4px rgba(0,0,0,0.25); white-space: nowrap;
        }
        .osm-price-label.leaflet-tooltip::before { display: none; } /* hide arrow */

        /* Dark mode: invert the raster tiles (OSM ships light-only). Markers,
           tooltips, and controls sit in other panes and stay untouched. */
        ${dark ? ".leaflet-tile-pane { filter: invert(1) hue-rotate(180deg) brightness(0.92) contrast(0.9) saturate(0.7); }" : ""}

        /* Grade badge markers — a letter-grade "button" per hospital. The badge
           scales with the zoom bucket class ZoomClassController sets on the
           container; the rank pill only appears zoomed in (osm-zoom-lg). */
        .osm-grade-marker { background: none; border: none; }
        .osm-grade-badge {
          display: flex; align-items: center; justify-content: center;
          width: 22px; height: 22px; border-radius: 50%;
          color: #fff; font-size: 12px; font-weight: 800; line-height: 1;
          font-family: ui-sans-serif, system-ui, sans-serif;
          border: 2px solid ${dark ? "rgba(255,255,255,0.85)" : "#fff"};
          box-shadow: 0 1px 4px rgba(0,0,0,0.4);
          transition: transform 0.12s, box-shadow 0.12s;
          cursor: pointer;
        }
        .osm-zoom-sm .osm-grade-badge { width: 15px; height: 15px; font-size: 9px; border-width: 1.5px; }
        .osm-zoom-lg .osm-grade-badge { width: 26px; height: 26px; font-size: 14px; }
        .osm-grade-marker:hover .osm-grade-badge { transform: scale(1.25); box-shadow: 0 2px 8px rgba(0,0,0,0.5); z-index: 10; }
        .osm-grade-marker.osm-selected .osm-grade-badge {
          transform: scale(1.3);
          box-shadow: 0 0 0 3px ${dark ? "rgba(197,165,90,0.65)" : "rgba(0,40,104,0.45)"}, 0 2px 8px rgba(0,0,0,0.5);
        }
        .osm-rank-pill {
          display: none;
          position: absolute; top: 100%; left: 50%; transform: translateX(-50%);
          margin-top: 2px; padding: 1px 5px; border-radius: 6px;
          background: ${dark ? "rgba(15,29,47,0.92)" : "rgba(255,255,255,0.95)"};
          color: ${panelInk}; border: 1px solid ${panelBorder};
          font-size: 9px; font-weight: 700; white-space: nowrap;
          box-shadow: 0 1px 3px rgba(0,0,0,0.3);
          font-family: ui-sans-serif, system-ui, sans-serif;
        }
        .osm-rank-pill .osm-rank-total { font-weight: 500; opacity: 0.65; }
        .osm-zoom-lg .osm-rank-pill, .osm-grade-marker.osm-selected .osm-rank-pill, .osm-grade-marker:hover .osm-rank-pill { display: block; }
        .osm-grade-marker:hover { z-index: 1000 !important; }
        /* Filtered-out markers dim but stay clickable — context, not clutter. */
        .osm-grade-marker.osm-dimmed { opacity: 0.22; }
        .osm-grade-marker.osm-dimmed:hover { opacity: 1; }
        .osm-grade-marker.osm-dimmed .osm-rank-pill { display: none; }
        /* Touch devices: expand the tap target well past the visual badge
           (badges are 15–26px; Apple HIG wants ~44px). Overlapping hit areas
           in dense clusters resolve to the topmost marker — acceptable. */
        @media (pointer: coarse) {
          .osm-grade-marker::before { content: ""; position: absolute; inset: -12px; }
        }
      `}</style>
      {/* "Offline map" badge so it's clear this is the OSM fallback. */}
      <div
        className="absolute z-[500] top-2 left-2 px-2 py-1 rounded-md text-[10px] font-semibold"
        style={{ backgroundColor: dark ? "rgba(15,29,47,0.9)" : "rgba(255,255,255,0.92)", color: dark ? "#C5A55A" : "#002868", border: `1px solid ${panelBorder}` }}
      >
        Map data © OpenStreetMap
      </div>
      {/* Price legend — bottom-right, above Leaflet's attribution. */}
      {legend.length > 0 && (
        <div className="absolute z-[500] rounded-lg px-2.5 py-2 shadow-lg"
          style={{ bottom: 22, ...(legendSide === "left" ? { left: 8 } : { right: 8 }), backgroundColor: panelBg, border: `1px solid ${panelBorder}`, backdropFilter: "blur(6px)" }}>
          <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", color: panelMuted, marginBottom: 4 }}>{legendTitle}</div>
          <div className="flex flex-col gap-1">
            {legend.map((it) => (
              <div key={it.label} className="flex items-center gap-1.5">
                <span style={{ width: 9, height: 9, borderRadius: 9, backgroundColor: it.color, border: "1px solid #fff", flexShrink: 0 }} />
                <span style={{ fontSize: 10, color: panelInk }}>{it.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {placing && (
        <div style={{ position: "absolute", inset: 0, zIndex: 500, display: "flex",
                      alignItems: "center", justifyContent: "center", borderRadius: 8,
                      background: dark ? "rgba(10,22,40,0.72)" : "rgba(246,241,229,0.78)", backdropFilter: "blur(2px)" }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10,
                        fontFamily: "'Instrument Sans', system-ui, sans-serif" }}>
            <div style={{ width: 34, height: 34, borderRadius: "50%",
                          border: "3px solid #C5A55A", borderTopColor: "transparent",
                          animation: "osm-spin 0.9s linear infinite" }} />
            <div style={{ fontSize: 12, fontWeight: 600, color: dark ? "#F5EBD7" : "#002868" }}>
              ★ Placing {valid.length.toLocaleString()} hospitals…
            </div>
          </div>
          <style>{`@keyframes osm-spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}
      <MapContainer center={center} zoom={4} scrollWheelZoom={scrollWheelZoom} whenReady={() => setTimeout(() => setPlacing(false), 120)} style={{ width: "100%", height: "100%", minHeight: 300, borderRadius: 8 }}>
        <MapViewController valid={valid} focusHospital={focusHospital} focusState={focusState} />
        <ZoomClassController />
        {onBackgroundClick && <MapClickBridge onClick={onBackgroundClick} />}
        <TileLayer url={tileUrl} attribution={attribution} />
        {(focusRadiusCenter || focusHospital) && focusRadiusMiles != null && focusRadiusMiles > 0 && (
          <Circle
            center={[(focusRadiusCenter || focusHospital)!.latitude, (focusRadiusCenter || focusHospital)!.longitude]}
            radius={focusRadiusMiles * 1609.344}
            pathOptions={{ color: dark ? "#C5A55A" : "#9A7B33", weight: 1.5, dashArray: "6 6", fillColor: dark ? "#C5A55A" : "#9A7B33", fillOpacity: 0.04 }}
            interactive={false}
          />
        )}
        {valid.map(h => {
          const color = h.color || colorFor(h.gross_charge || 0, avgPrice);
          const priceShown = h.scenarioPrice ?? h.gross_charge;
          // Summary card — the consumer "poster". Rows render only when the
          // data exists; no N/A walls.
          const ownBucket = ownershipBucket(h.ownership);
          const factBits: string[] = [];
          if (OWNERSHIP_LABEL[ownBucket]) factBits.push(OWNERSHIP_LABEL[ownBucket]);
          if (h.bedCount) factBits.push(`${h.bedCount.toLocaleString()} beds`);
          if (h.emergencyServices === true) factBits.push("ER ✓");
          else if (h.emergencyServices === false) factBits.push("No ER");
          const starBits: string[] = [];
          if (h.cmsStars != null) starBits.push(`CMS ${"★".repeat(Math.round(h.cmsStars))}${"☆".repeat(5 - Math.round(h.cmsStars))}`);
          if (h.googleRating != null) starBits.push(`Google ${h.googleRating.toFixed(1)}${h.googleReviews ? ` (${h.googleReviews.toLocaleString()})` : ""}`);
          // CMS comparative quality groups — ↑ better / = same / ↓ worse than
          // the national average (CMS normalizes direction across groups).
          const qualDir = (v: string | null | undefined): { sym: string; color: string } | null => {
            if (!v) return null;
            const s = v.toLowerCase();
            if (s.startsWith("above")) return { sym: "↑", color: "#16a34a" };
            if (s.startsWith("below")) return { sym: "↓", color: "#dc2626" };
            if (s.startsWith("same")) return { sym: "=", color: "#6b7280" };
            return null;
          };
          const qualBits = ([
            ["Safety", qualDir(h.cmsSafety)],
            ["Mortality", qualDir(h.cmsMortality)],
            ["Readmits", qualDir(h.cmsReadmission)],
            ["Experience", qualDir(h.cmsExperience)],
          ] as const).filter((q): q is [string, { sym: string; color: string }] => q[1] != null);
          const popup = disablePopups ? null : (
            <Popup maxWidth={280}>
              <div style={{ minWidth: 210, fontFamily: "ui-sans-serif, system-ui, sans-serif" }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                  {h.grade && (
                    <span style={{
                      display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                      width: 26, height: 26, borderRadius: 13, backgroundColor: gradeColor(h.grade),
                      color: "#fff", fontSize: 14, fontWeight: 800, marginTop: 1,
                    }}>{h.grade}</span>
                  )}
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 13, lineHeight: 1.25 }}>{h.hospital_name}</div>
                    <div style={{ fontSize: 11, color: "#6b7280" }}>
                      {h.rank != null && (
                        <span style={{ fontWeight: 700, color: "#374151" }}>
                          #{h.rank.toLocaleString()}{totalRanked ? ` of ${totalRanked.toLocaleString()}` : ""}
                        </span>
                      )}
                      {h.rank != null && (h.city || h.state) ? " · " : ""}
                      {[h.city, h.state].filter(Boolean).join(", ")}
                    </div>
                  </div>
                </div>
                {priceShown != null && priceShown > 0 && (
                  <div style={{ fontSize: 18, fontWeight: 800, color, marginTop: 6 }}>
                    ${Math.round(priceShown).toLocaleString()}
                    {h.scenarioPrice != null && <span style={{ fontSize: 10, fontWeight: 600, color: "#6b7280" }}> {priceLabel}</span>}
                  </div>
                )}
                {starBits.length > 0 && (
                  <div style={{ fontSize: 11, color: "#374151", marginTop: 5 }}>{starBits.join(" · ")}</div>
                )}
                {qualBits.length > 0 && (
                  <div style={{ fontSize: 10, color: "#6b7280", marginTop: 3 }}>
                    {qualBits.map(([label, d], i) => (
                      <span key={label}>
                        {i > 0 && " · "}
                        {label} <span style={{ color: d.color, fontWeight: 800 }}>{d.sym}</span>
                      </span>
                    ))}
                    <span style={{ opacity: 0.7 }}> vs natl avg</span>
                  </div>
                )}
                {factBits.length > 0 && (
                  <div style={{ fontSize: 11, color: "#374151", marginTop: 3 }}>{factBits.join(" · ")}</div>
                )}
                {h.hospitalSystem && (
                  <div style={{ fontSize: 10, color: "#6b7280", marginTop: 3 }}>Part of {h.hospitalSystem}</div>
                )}
                <a
                  href={`/hospital/${h.hospital_id}`}
                  style={{ display: "inline-block", marginTop: 7, fontSize: 12, fontWeight: 700, color: "#2563eb", textDecoration: "none" }}
                >
                  View full prices →
                </a>
              </div>
            </Popup>
          );

          // Grade badge "button" marker — letter grade + rank pill (zoomed in).
          if (h.grade) {
            const rankHtml = h.rank != null
              ? `<div class="osm-rank-pill">#${h.rank.toLocaleString()}${totalRanked ? `<span class="osm-rank-total"> of ${abbrevCount(totalRanked)}</span>` : ""}</div>`
              : "";
            const icon = L.divIcon({
              className: `osm-grade-marker${selectedId === h.hospital_id ? " osm-selected" : ""}${h.dimmed ? " osm-dimmed" : ""}`,
              html: `<div class="osm-grade-badge" style="background:${gradeColor(h.grade)}">${h.grade}</div>${rankHtml}`,
              iconSize: [26, 26],
              iconAnchor: [13, 13],
            });
            return (
              <Marker
                key={h.hospital_id}
                ref={(m) => { if (m) markerRefs.current.set(h.hospital_id, m); else markerRefs.current.delete(h.hospital_id); }}
                position={[h.latitude, h.longitude]}
                icon={icon}
                eventHandlers={onSelect ? { click: () => onSelect(h.hospital_id) } : undefined}
              >
                {popup}
              </Marker>
            );
          }

          // Plain dot (no grade data — ungraded/unranked hospitals).
          return (
            <CircleMarker
              key={h.hospital_id}
              ref={(m) => { if (m) markerRefs.current.set(h.hospital_id, m); else markerRefs.current.delete(h.hospital_id); }}
              center={[h.latitude, h.longitude]}
              radius={6}
              pathOptions={{ color: "#fff", weight: 1, fillColor: color, fillOpacity: h.dimmed ? 0.18 : 0.9, opacity: h.dimmed ? 0.18 : 1, bubblingMouseEvents: false }}
              eventHandlers={onSelect ? { click: () => onSelect(h.hospital_id) } : undefined}
            >
              {/* Permanent price label on the dot — see the number without clicking. */}
              {priceLabelsOn && h.gross_charge != null && h.gross_charge > 0 && (
                <Tooltip permanent direction="top" offset={[0, -6]} className="osm-price-label">
                  {abbrevMoney(h.gross_charge)}
                </Tooltip>
              )}
              {popup}
            </CircleMarker>
          );
        })}
      </MapContainer>
    </div>
  );
}
